import {inject, injectable} from 'inversify';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {ResponsePipe} from '../utils/request.utils';
import {ApiConfig} from './api-config';
import {LightHttp} from './light-http.service';
import {LoginService} from './login.service';

/**
 * Single row in the portal conversion-history view.
 *
 * Field names mirror the professorx `ConversionHistoryController` JSON
 * shape verbatim — keeping the wire format opaque means the UI never
 * mis-translates a field rename.
 */
export interface ConversionHistoryEvent {
    timestamp: string | null;
    eventType: 'JOB_COMPLETED' | 'BLOCKED' | 'JOB_ABORTED' | string;
    workflowSource: string | null;
    matchedPattern: string | null;
    violation: string | null;
    userMessage: string | null;
    workflowId: string | null;
    currentValue: number | null;
    limitValue: number | null;
    cpuSeconds: number | null;
    estimatedCredits: number | null;
    success: boolean | null;
    reason: string | null;
    tier: string | null;
    fromExtension: string | null;
    fromMedia: string | null;
    toExtension: string | null;
    toMedia: string | null;
    processType: string | null;
}

export interface ConversionHistoryResponse {
    limit: number;
    source: 'website' | 'api_key' | null;
    count: number;
    events: ConversionHistoryEvent[];
}

/**
 * Portal-side service for the "Conversion history" page.
 *
 * Wraps `GET /v1/api/portal/conversion-history`. The endpoint is
 * session-authenticated (JWT bearer), so anonymous reads are rejected
 * server-side.
 */
@injectable()
export class ConversionHistoryService {
    constructor(
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(LightHttp) private http: LightHttp,
        @inject(LoginService) private loginService: LoginService,
    ) {}

    /**
     * Fetch the recent conversion events for the signed-in user.
     *
     * @param source optional filter — render the Website column with
     *   `'website'`, the API column with `'api_key'`. Omitting it returns
     *   both interleaved (newest-first).
     * @param limit  capped server-side at 200.
     */
    list(
        source?: 'website' | 'api_key',
        limit = 50,
        from?: string,
        until?: string,
    ): Observable<ConversionHistoryResponse> {
        this.requireLogin();
        const url = this.apiConfig.getConversionHistoryEndpoint(source, limit, from, until);
        return this.http.get<ConversionHistoryResponse>(url).pipe(ResponsePipe);
    }

    private requireLogin(): void {
        if (!this.loginService.isUserLoggedIn) {
            throw new Error('Please sign in to view your conversion history.');
        }
    }
}
